<script setup lang="ts">
// types
import type { SectionHeroImageProps } from '@ts-sections/section-hero-image/interfaces'
// hooks
import { useSectionHeroImage } from '@hooks/sections/section-hero-image.hook'
// helpers
import { STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'
// components
import { Button } from '@brighterly/lib-lumee-ui'
import { AppImage } from '@UI/Images/AppImage'
import BlockStatsItem from '@Blocks/BlockStatsItem.vue'
import BlockMediaCard from '@Blocks/BlockMediaCard.vue'
import TrustpilotWidget from '@Widgets/Trustpilot/TrustpilotWidget.vue'

defineOptions({
  name: 'SectionHeroImage',
})

const props = defineProps<{
  section: SectionHeroImageProps
}>()

const {
  refTitle,
  refSubtitle,
  refImageAlt,
  refMediaCard,
  refHasVideo,
  refCtas,
  refStats,
  refShowStats,
  handleClickCta,
} = useSectionHeroImage({
  section: () => props.section,
})
</script>

<template>
  <section
    class="section-hero-image"
    :data-component="section.__component"
  >
    <div class="section-hero-image__surface">
      <div class="section-hero-image__columns">
        <TrustpilotWidget
          view="full"
          class="section-hero-image__trustpilot section-hero-image__trustpilot--stack"
        />

        <div
          class="section-hero-image__media"
          :class="{ 'section-hero-image__media--video': refHasVideo }"
        >
          <BlockMediaCard
            v-if="refHasVideo && refMediaCard"
            :block="refMediaCard"
          />
          <AppImage
            v-else-if="section.image"
            class="section-hero-image__image"
            :media="section.image"
            :sizes="STRAPI_IMAGE_SIZES"
            :alt="refImageAlt"
            priority
          />
        </div>

        <div class="section-hero-image__copy">
          <div class="section-hero-image__text">
            <h1 class="section-hero-image__title btly-text-heading-m md:btly-text-heading-l lg:btly-text-heading-xl">
              {{ refTitle }}
            </h1>
            <p
              v-if="refSubtitle"
              class="section-hero-image__subtitle btly-text-body-m md:btly-text-body-l"
            >
              {{ refSubtitle }}
            </p>
            <TrustpilotWidget
              view="full"
              class="section-hero-image__trustpilot section-hero-image__trustpilot--copy"
            />
          </div>

          <ul
            v-if="refCtas.length"
            class="section-hero-image__cta-list"
          >
            <li
              v-for="cta in refCtas"
              :key="cta.key"
              class="section-hero-image__cta-item"
            >
              <a
                class="section-hero-image__cta-link"
                :href="cta.href"
                @click="handleClickCta(cta)"
              >
                <Button
                  :view="cta.view"
                  size="md"
                  :text="cta.text"
                  tabindex="-1"
                />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      v-if="refShowStats"
      class="section-hero-image__stats"
    >
      <ul class="section-hero-image__stats-list">
        <li
          v-for="(stat, index) in refStats"
          :key="stat.id ?? index"
          class="section-hero-image__stats-item"
        >
          <BlockStatsItem :block="stat" />
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped lang="scss" src="@scss/components/Sections/SectionHeroImage.scss" />
